/**
 * LayoutEditor — direct-manipulation canvas for a channel's on-screen layout.
 *
 * A true-9:16 preview over a fixed, neutral sample frame (the same still for
 * every brand, so the layout reads clearly) with three draggable layers —
 * Caption, Logo, Speaker card — plus a modern segmented layer picker and a
 * per-layer property panel. Positions are set by DRAG, not numeric px fields;
 * style (font size, opacity, box, …) lives in the panel.
 *
 * Geometry is faithful to the real 2160×3840 render canvas: sizes/offsets are
 * expressed in container-query units (cqw/cqh) against a `container-type` frame,
 * exactly like ChannelPreview, so what the operator drags is what the pipeline
 * renders. Drag inverts that mapping (client px → canvas %/px), see applyMove().
 */
import { Captions, Image as ImageIcon, Move, User } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import previewSampleUrl from "../assets/preview-sample.jpg";
import { useIsMobile } from "../lib/useIsMobile";
import { Select } from "./ui/Select";

const CANVAS_W = 2160;
const CANVAS_H = 3840;
/** Bottom action-safe line (fraction from top). Layers magnet-snap near it. */
const SAFE_FRAC = 0.9;
const SAFE_BOTTOM_PX = Math.round((1 - SAFE_FRAC) * CANVAS_H); // 384
const CENTER_SNAP = 0.02; // ±2% of canvas width → snap to exact centre
const SAFE_SNAP = 0.012; // ±1.2% of canvas height → snap to safe line

export type LayerId = "caption" | "logo" | "speaker";

/** Every draggable/tunable value the editor owns. Mirrors Onboard's flat state. */
export interface LayoutValues {
  // caption
  capBottomPx: number;
  capFontSizePx: number;
  /** Two-line mode: independent top & punch line sizes (no derived multiplier). */
  capTopPx: number;
  capPunchPx: number;
  capUniform: boolean;
  capBox: boolean;
  capBoxOpacity: number;
  capFlatText: boolean;
  capSidePaddingPx: number;
  capLineHeight: number;
  /** CSS letter-spacing in px (negative = tighter). Founding default 0.5. */
  capLetterSpacingPx: number;
  /** Editable per-line caption colours: top line + uniform use captionColor; punch line uses captionAccent. */
  captionColor: string;
  captionAccent: string;
  /** Per-element font FAMILY ("" = auto → the channel's single uploaded font or
   *  founding default). Families are @font-face-registered by the parent. */
  fontCaption: string;
  fontPunch: string;
  fontSpeaker: string;
  // logo
  logoWidthPct: number;
  logoBottomPx: number;
  logoOpacity: number;
  logoXPct: number;
  // speaker card (top-left anchor, % of canvas)
  speakerCardXPct: number;
  speakerCardYPct: number;
  /** Size multiplier over the founding card (1 = founding 35% width). */
  speakerCardScale: number;
}

interface Visual {
  captionColor: string;
  captionAccent: string;
  speakerNameColor: string;
  watermark?: string;
  fontFamily?: string;
  fontDataUrl?: string | null;
  direction?: "rtl" | "ltr";
  sampleTop?: string;
  samplePunch?: string;
  sampleSpeaker?: { name: string; role: string };
}

export interface LayoutEditorProps {
  values: LayoutValues;
  onChange: (patch: Partial<LayoutValues>) => void;
  selectedLayer: LayerId | null;
  onSelectLayer: (l: LayerId | null) => void;
  /** Pre-selected layer when the editor mounts (per wizard step). */
  focusLayer?: LayerId;
  /** Brand UI accent — drives selection chrome, guides, sliders. */
  accent: string;
  logoDataUrl: string | null;
  visual: Visual;
  /** Font families the panel dropdowns may assign per element (upload +
   *  existing + founding). Empty array hides the dropdowns. */
  fontOptions?: string[];
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
const round2 = (v: number): number => Math.round(v * 100) / 100;

function hexToRgba(hex: string, a: number): string {
  const m = hex.replace("#", "");
  const n = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  const r = parseInt(n.slice(0, 2), 16) || 0;
  const g = parseInt(n.slice(2, 4), 16) || 0;
  const b = parseInt(n.slice(4, 6), 16) || 0;
  return `rgba(${r},${g},${b},${a})`;
}

/** Scoped styling for the range sliders + selection/guide motion. Injected once. */
const LE_CSS = `
.le-range{ -webkit-appearance:none; appearance:none; width:100%; height:6px; border-radius:999px; outline:none; cursor:pointer; }
.le-range::-webkit-slider-thumb{ -webkit-appearance:none; appearance:none; width:18px; height:18px; border-radius:50%; background:var(--le-accent,#FFD500); border:2px solid #0a0a0b; box-shadow:0 2px 8px rgba(0,0,0,.55); cursor:grab; transition:transform .12s var(--ql-ease); }
.le-range::-webkit-slider-thumb:active{ cursor:grabbing; transform:scale(1.15); }
.le-range::-moz-range-thumb{ width:18px; height:18px; border-radius:50%; background:var(--le-accent,#FFD500); border:2px solid #0a0a0b; box-shadow:0 2px 8px rgba(0,0,0,.55); cursor:grab; }
.le-range:focus-visible{ box-shadow:0 0 0 3px rgba(255,213,0,.30); }
.le-selframe{ animation:le-pop .16s var(--ql-ease); }
@keyframes le-pop{ from{ opacity:0; transform:scale(.94); } to{ opacity:1; transform:scale(1); } }
.le-chip{ transition: background .15s var(--ql-ease), border-color .15s var(--ql-ease), color .15s var(--ql-ease); }
@media (prefers-reduced-motion: reduce){
  .le-range::-webkit-slider-thumb{ transition:none; }
  .le-selframe{ animation:none; }
  .le-chip{ transition:none; }
}
`;

export function LayoutEditor(props: LayoutEditorProps): React.JSX.Element {
  const { values, onChange, selectedLayer, onSelectLayer, focusLayer, accent, logoDataUrl, visual, fontOptions = [] } = props;
  const isMobile = useIsMobile();

  const {
    captionColor, speakerNameColor, watermark,
    fontFamily, fontDataUrl,
    direction = "rtl",
    sampleTop = "لن تحقق شيئًا إن لم", samplePunch = "تطلب ما تريد",
    sampleSpeaker = { name: "نولان جوكوفيتش", role: "لاعب تنس محترف" },
  } = visual;
  const isRtl = direction === "rtl";
  const useFont = !!(fontFamily && fontDataUrl);
  const fontStack = useFont ? `"${fontFamily}", "Segoe UI", sans-serif` : "inherit";
  /** Per-role font stack — set family wins, else the frame's single-font stack. */
  const roleStack = (fam: string): string | undefined =>
    fam ? `"${fam}", "Segoe UI", sans-serif` : undefined;

  const cqw = (px: number): string => `${(px / CANVAS_W) * 100}cqw`;
  const cqh = (px: number): string => `${(px / CANVAS_H) * 100}cqh`;
  const shadow = values.capFlatText ? "none" : "0 0.3cqh 0.9cqh rgba(0,0,0,.85)";

  // Pre-select the focus layer whenever the step (and thus focusLayer) changes.
  // onSelectLayer is a stable useState setter from the parent, so this is safe.
  useEffect(() => {
    onSelectLayer(focusLayer ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusLayer]);

  const frameRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ layer: LayerId; grabDx: number; grabDy: number; el: HTMLElement } | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastPtr = useRef<{ x: number; y: number } | null>(null);
  const [dragging, setDragging] = useState<LayerId | null>(null);
  const [hovered, setHovered] = useState<LayerId | null>(null);
  const [guides, setGuides] = useState<{ vCenter: boolean; hCenter: boolean; bottomSafe: boolean }>({ vCenter: false, hCenter: false, bottomSafe: false });
  // Which caption line the property panel is editing (two-line mode). Click a line
  // on the canvas — or the Line 1 / Line 2 tabs — to switch. Uniform mode ignores it.
  const [capLine, setCapLine] = useState<"top" | "punch">("punch");

  /** Current "control point" of a layer, in [0,1] frame fractions. */
  const controlFrac = useCallback((layer: LayerId): { x: number; y: number } => {
    if (layer === "caption") return { x: 0.5, y: 1 - clamp(values.capBottomPx, 0, CANVAS_H) / CANVAS_H };
    if (layer === "logo") return { x: values.logoXPct / 100, y: 1 - clamp(values.logoBottomPx, 0, CANVAS_H) / CANVAS_H };
    return { x: values.speakerCardXPct / 100, y: values.speakerCardYPct / 100 };
  }, [values.capBottomPx, values.logoXPct, values.logoBottomPx, values.speakerCardXPct, values.speakerCardYPct]);

  const applyMove = useCallback(() => {
    rafRef.current = null;
    const d = dragRef.current;
    const p = lastPtr.current;
    const frame = frameRef.current;
    if (!d || !p || !frame) return;
    const rect = frame.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    let fx = clamp((p.x - rect.left) / rect.width - d.grabDx, 0, 1);
    let fy = clamp((p.y - rect.top) / rect.height - d.grabDy, 0, 1);
    let vCenter = false;
    let hCenter = false;
    let bottomSafe = false;

    if (d.layer === "caption") {
      // Caption is horizontally centre-locked → always mark the vertical-centre line.
      vCenter = true;
      let bottomPx = Math.round((1 - fy) * CANVAS_H);
      if (Math.abs(fy - SAFE_FRAC) < SAFE_SNAP) { bottomPx = SAFE_BOTTOM_PX; bottomSafe = true; }
      else if (Math.abs(fy - 0.5) < CENTER_SNAP) { bottomPx = Math.round(CANVAS_H * 0.5); hCenter = true; }
      onChange({ capBottomPx: clamp(bottomPx, 0, 2400) });
    } else if (d.layer === "logo") {
      let xPct = fx * 100;
      if (Math.abs(fx - 0.5) < CENTER_SNAP) { xPct = 50; vCenter = true; }
      xPct = clamp(xPct, 5, 95);
      let bottomPx = Math.round((1 - fy) * CANVAS_H);
      if (Math.abs(fy - SAFE_FRAC) < SAFE_SNAP) { bottomPx = SAFE_BOTTOM_PX; bottomSafe = true; }
      else if (Math.abs(fy - 0.5) < CENTER_SNAP) { bottomPx = Math.round(CANVAS_H * 0.5); hCenter = true; }
      onChange({ logoXPct: round2(xPct), logoBottomPx: clamp(bottomPx, 0, 2000) });
    } else {
      // Speaker card is top-left anchored; snap its measured CENTRE to canvas centre.
      const halfWFrac = d.el.offsetWidth / rect.width / 2;
      const halfHFrac = d.el.offsetHeight / rect.height / 2;
      let xFrac = fx;
      if (Math.abs(xFrac + halfWFrac - 0.5) < CENTER_SNAP) { xFrac = 0.5 - halfWFrac; vCenter = true; }
      let yFrac = fy;
      if (Math.abs(yFrac + halfHFrac - 0.5) < CENTER_SNAP) { yFrac = 0.5 - halfHFrac; hCenter = true; }
      onChange({ speakerCardXPct: round2(clamp(xFrac * 100, 0, 92)), speakerCardYPct: round2(clamp(yFrac * 100, 0, 92)) });
    }
    setGuides((g) => (g.vCenter === vCenter && g.hCenter === hCenter && g.bottomSafe === bottomSafe ? g : { vCenter, hCenter, bottomSafe }));
  }, [onChange]);

  const startDrag = useCallback((layer: LayerId, e: React.PointerEvent<HTMLElement>): void => {
    e.stopPropagation();
    onSelectLayer(layer);
    const frame = frameRef.current;
    if (!frame) return;
    const rect = frame.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const fx = (e.clientX - rect.left) / rect.width;
    const fy = (e.clientY - rect.top) / rect.height;
    const ctrl = controlFrac(layer);
    dragRef.current = { layer, grabDx: fx - ctrl.x, grabDy: fy - ctrl.y, el: e.currentTarget };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* older browsers */ }
    setDragging(layer);
  }, [controlFrac, onSelectLayer]);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLElement>): void => {
    if (!dragRef.current) return;
    lastPtr.current = { x: e.clientX, y: e.clientY };
    if (rafRef.current == null) rafRef.current = requestAnimationFrame(applyMove);
  }, [applyMove]);

  const endDrag = useCallback((e: React.PointerEvent<HTMLElement>): void => {
    const d = dragRef.current;
    if (d) { try { d.el.releasePointerCapture(e.pointerId); } catch { /* noop */ } }
    dragRef.current = null;
    if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    setDragging(null);
    setGuides({ vCenter: false, hCenter: false, bottomSafe: false });
  }, []);

  useEffect(() => () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current); }, []);

  // ---- Resize via the selection handles (corners = size, caption sides = width) ----
  const resizeRef = useRef<{
    layer: LayerId; handle: string; cx: number; cy: number; startDist: number;
    startW: number; startScale: number; startCap: number;
    capKey: "capFontSizePx" | "capTopPx" | "capPunchPx"; capMin: number; capMax: number;
  } | null>(null);

  const startResize = useCallback((layer: LayerId, handle: string, e: React.PointerEvent<HTMLElement>): void => {
    e.stopPropagation();
    const layerEl = (e.currentTarget as HTMLElement).closest("[data-layer]");
    if (!layerEl) return;
    const box = layerEl.getBoundingClientRect();
    const cx = box.left + box.width / 2;
    const cy = box.top + box.height / 2;
    const capKey = values.capUniform ? "capFontSizePx" : capLine === "top" ? "capTopPx" : "capPunchPx";
    const [capMin, capMax] = values.capUniform ? [50, 260] : capLine === "top" ? [40, 200] : [60, 340];
    resizeRef.current = {
      layer, handle, cx, cy,
      startDist: Math.max(4, Math.hypot(e.clientX - cx, e.clientY - cy)),
      startW: values.logoWidthPct, startScale: values.speakerCardScale,
      startCap: values[capKey], capKey, capMin, capMax,
    };
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* older browsers */ }
  }, [values, capLine]);

  const onResizeMove = useCallback((e: React.PointerEvent<HTMLElement>): void => {
    const r = resizeRef.current;
    const frame = frameRef.current;
    if (!r || !frame) return;
    if (r.layer === "caption" && (r.handle === "ml" || r.handle === "mr")) {
      // Side handles set the caption band width via sidePaddingPx, symmetric.
      const rect = frame.getBoundingClientRect();
      if (rect.width === 0) return;
      const halfFrac = Math.abs((e.clientX - rect.left) / rect.width - 0.5);
      const pad = Math.round(((0.5 - halfFrac) * CANVAS_W) / 10) * 10;
      onChange({ capSidePaddingPx: clamp(pad, 20, 400) });
      return;
    }
    const ratio = Math.hypot(e.clientX - r.cx, e.clientY - r.cy) / r.startDist;
    if (r.layer === "caption") {
      const v = Math.round(clamp(r.startCap * ratio, r.capMin, r.capMax) / 5) * 5;
      onChange({ [r.capKey]: v } as Partial<LayoutValues>);
    } else if (r.layer === "logo") {
      onChange({ logoWidthPct: Math.round(clamp(r.startW * ratio, 5, 80)) });
    } else {
      onChange({ speakerCardScale: round2(clamp(r.startScale * ratio, 0.5, 2)) });
    }
  }, [onChange]);

  const endResize = useCallback((e: React.PointerEvent<HTMLElement>): void => {
    if (!resizeRef.current) return;
    resizeRef.current = null;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* noop */ }
  }, []);

  /** Handler bag for a layer's selection handles. */
  const resizeHandlers = useCallback((layer: LayerId) => ({
    onDown: (h: string, e: React.PointerEvent<HTMLElement>) => startResize(layer, h, e),
    onMove: onResizeMove,
    onUp: endResize,
  }), [startResize, onResizeMove, endResize]);

  /** Shared handlers bag for a draggable layer. */
  const dragHandlers = (layer: LayerId): React.HTMLAttributes<HTMLDivElement> => ({
    onPointerDown: (e) => startDrag(layer, e),
    onPointerMove,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
    onPointerEnter: () => setHovered(layer),
    onPointerLeave: () => setHovered((h) => (h === layer ? null : h)),
  });

  /** Selected/hover chrome merged into a layer wrapper's inline style. */
  const chromeStyle = (layer: LayerId): React.CSSProperties => {
    const selected = selectedLayer === layer;
    const hoveredNow = hovered === layer && !selected && dragging == null;
    return {
      cursor: dragging === layer ? "grabbing" : layer === "caption" ? "ns-resize" : "grab",
      touchAction: "none",
      outline: hoveredNow ? `1px dashed ${hexToRgba(accent, 0.55)}` : undefined,
      outlineOffset: hoveredNow ? 4 : undefined,
    };
  };

  const topPx = values.capTopPx;
  const bottomPunchPx = values.capPunchPx;

  return (
    <div style={{ display: "grid", gap: 18 }}>
      <style>{LE_CSS}</style>
      {useFont && (
        <style>{`@font-face { font-family: "${fontFamily}"; src: url(${fontDataUrl}); font-display: swap; }`}</style>
      )}

      {/* ---- Layer picker (segmented control) ---- */}
      <div style={{ display: "flex", gap: 6, padding: 6, borderRadius: 16, background: "rgba(255,255,255,.03)", border: "1px solid rgba(255,255,255,.06)" }}>
        <LayerChip icon={<Captions size={15} />} label="Caption" active={selectedLayer === "caption"} accent={accent} onClick={() => onSelectLayer("caption")} />
        <LayerChip icon={<ImageIcon size={15} />} label="Logo" active={selectedLayer === "logo"} accent={accent} onClick={() => onSelectLayer("logo")} />
        <LayerChip icon={<User size={15} />} label="Speaker" active={selectedLayer === "speaker"} accent={accent} onClick={() => onSelectLayer("speaker")} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(280px, 360px) minmax(0, 1fr)", gap: 24, alignItems: "start" }}>
        {/* ---- Canvas ---- */}
        <div style={{ width: "100%", maxWidth: 360, marginInline: isMobile ? "auto" : undefined }}>
          <div
            ref={frameRef}
            onPointerDown={(e) => { if (e.target === e.currentTarget) onSelectLayer(null); }}
            style={{
              position: "relative",
              width: "100%",
              aspectRatio: `${CANVAS_W} / ${CANVAS_H}`,
              containerType: "size",
              borderRadius: 16,
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,.1)",
              background: `#000 center / cover no-repeat url(${previewSampleUrl})`,
              fontFamily: fontStack,
              userSelect: "none",
              touchAction: "none",
              cursor: dragging ? "grabbing" : "default",
            }}
          >
            {/* Legibility scrim — must not intercept pointer events. */}
            <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "linear-gradient(to top, rgba(0,0,0,.6), rgba(0,0,0,.05) 45%, rgba(0,0,0,.25))" }} />

            {/* Centre crosshair — faint while dragging, bright when snapped to centre */}
            {dragging && (
              <>
                <div style={{ position: "absolute", top: 0, bottom: 0, left: "50%", width: guides.vCenter ? 2 : 1, transform: "translateX(-50%)", background: hexToRgba(accent, guides.vCenter ? 0.95 : 0.32), boxShadow: guides.vCenter ? `0 0 8px ${hexToRgba(accent, 0.7)}` : "none", pointerEvents: "none" }} />
                <div style={{ position: "absolute", left: 0, right: 0, top: "50%", height: guides.hCenter ? 2 : 1, transform: "translateY(-50%)", background: hexToRgba(accent, guides.hCenter ? 0.95 : 0.32), boxShadow: guides.hCenter ? `0 0 8px ${hexToRgba(accent, 0.7)}` : "none", pointerEvents: "none" }} />
              </>
            )}
            {guides.bottomSafe && (
              <div style={{ position: "absolute", left: 0, right: 0, top: `${SAFE_FRAC * 100}%`, height: 0, borderTop: `1.5px dashed ${hexToRgba(accent, 0.8)}`, pointerEvents: "none" }} />
            )}

            {/* Speaker card */}
            <div
              dir={direction}
              data-layer="speaker"
              {...dragHandlers("speaker")}
              style={{
                position: "absolute",
                top: `${values.speakerCardYPct}cqh`,
                left: `${values.speakerCardXPct}cqw`,
                textAlign: isRtl ? "right" : "left",
                ...chromeStyle("speaker"),
              }}
            >
              <div style={{ color: speakerNameColor, fontWeight: 700, fontSize: cqh(150 * values.speakerCardScale), textShadow: shadow, whiteSpace: "nowrap", fontFamily: roleStack(values.fontSpeaker) }}>{sampleSpeaker.name}</div>
              <div style={{ color: captionColor, fontSize: cqh(78 * values.speakerCardScale), opacity: 0.9, textShadow: shadow, marginTop: "0.4cqh", whiteSpace: "nowrap", fontFamily: roleStack(values.fontSpeaker) }}>{sampleSpeaker.role}</div>
              {selectedLayer === "speaker" && <SelectionChrome accent={accent} resize={resizeHandlers("speaker")} />}
            </div>

            {/* Logo (real upload, or a placeholder box so it's still positionable) */}
            <div
              data-layer="logo"
              {...dragHandlers("logo")}
              style={{
                position: "absolute",
                bottom: cqh(values.logoBottomPx),
                left: `${values.logoXPct}cqw`,
                transform: "translateX(-50%)",
                width: `${values.logoWidthPct}cqw`,
                ...chromeStyle("logo"),
              }}
            >
              {logoDataUrl ? (
                <img src={logoDataUrl} alt="logo" style={{ width: "100%", opacity: values.logoOpacity, objectFit: "contain", display: "block", pointerEvents: "none" }} />
              ) : (
                <div style={{ width: "100%", aspectRatio: "2.4 / 1", border: `1px dashed ${hexToRgba(accent, 0.5)}`, borderRadius: "1.2cqh", display: "flex", alignItems: "center", justifyContent: "center", color: hexToRgba(accent, 0.8), fontSize: cqh(70), fontWeight: 700, letterSpacing: "0.1em", pointerEvents: "none" }}>LOGO</div>
              )}
              {selectedLayer === "logo" && <SelectionChrome accent={accent} resize={resizeHandlers("logo")} />}
            </div>

            {/* Caption */}
            <div
              dir={direction}
              data-layer="caption"
              {...dragHandlers("caption")}
              style={{
                position: "absolute",
                bottom: cqh(values.capBottomPx),
                left: cqw(values.capSidePaddingPx),
                right: cqw(values.capSidePaddingPx),
                textAlign: "center",
                lineHeight: values.capLineHeight,
                ...(values.capBox ? { background: `rgba(0,0,0,${values.capBoxOpacity})`, borderRadius: "1.2cqh", padding: "0.8cqh 2cqw" } : {}),
                ...chromeStyle("caption"),
              }}
            >
              {values.capUniform ? (
                <div style={{ color: values.captionColor, fontWeight: 700, fontSize: cqh(values.capFontSizePx), textShadow: shadow, letterSpacing: `${values.capLetterSpacingPx}px`, fontFamily: roleStack(values.fontCaption) }}>
                  {sampleTop} {samplePunch}
                </div>
              ) : (
                <>
                  <div
                    onPointerDown={() => { onSelectLayer("caption"); setCapLine("top"); }}
                    style={{ color: values.captionColor, fontWeight: 700, fontSize: cqh(topPx), textShadow: shadow, letterSpacing: `${values.capLetterSpacingPx}px`, fontFamily: roleStack(values.fontCaption), cursor: "pointer", outline: selectedLayer === "caption" && capLine === "top" ? `2px solid ${accent}` : undefined, outlineOffset: 3, borderRadius: 3 }}
                  >{sampleTop}</div>
                  <div
                    onPointerDown={() => { onSelectLayer("caption"); setCapLine("punch"); }}
                    style={{ color: values.captionAccent, fontWeight: 700, fontSize: cqh(bottomPunchPx), textShadow: shadow, letterSpacing: `${values.capLetterSpacingPx}px`, fontFamily: roleStack(values.fontPunch), cursor: "pointer", outline: selectedLayer === "caption" && capLine === "punch" ? `2px solid ${accent}` : undefined, outlineOffset: 3, borderRadius: 3 }}
                  >{samplePunch}</div>
                </>
              )}
              {selectedLayer === "caption" && <SelectionChrome accent={accent} sideHandles resize={resizeHandlers("caption")} />}
            </div>

            {/* Watermark — decorative, not a layer */}
            {watermark && (
              <div style={{ position: "absolute", bottom: cqh(90), left: "50%", transform: "translateX(-50%)", color: captionColor, opacity: 0.7, fontSize: cqh(58), textShadow: shadow, pointerEvents: "none" }}>
                {watermark}
              </div>
            )}
          </div>

          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "var(--ql-fg-subtle)" }}>
            <Move size={13} />
            <span>Drag to move. Click a caption line to select &amp; style it.</span>
          </div>
        </div>

        {/* ---- Property panel ---- */}
        <PropertyPanel
          layer={selectedLayer}
          values={values}
          onChange={onChange}
          accent={accent}
          logoDataUrl={logoDataUrl}
          capLine={capLine}
          onCapLine={setCapLine}
          fontOptions={fontOptions}
        />
      </div>
    </div>
  );
}

export interface ResizeHandlerBag {
  onDown: (h: string, e: React.PointerEvent<HTMLElement>) => void;
  onMove: (e: React.PointerEvent<HTMLElement>) => void;
  onUp: (e: React.PointerEvent<HTMLElement>) => void;
}

/** Selection frame with LIVE resize handles: corners scale the element, and the
 *  caption's side handles set its band width. `resize` absent → decorative.
 *  Exported for reuse by the Studio canvas overlay (same selection feel on
 *  the live player as on the onboarding layout canvas). */
export function SelectionChrome({ accent, sideHandles = false, resize }: {
  accent: string;
  sideHandles?: boolean;
  resize?: ResizeHandlerBag;
}): React.JSX.Element {
  const handle = (h: string, pos: React.CSSProperties, cursor: string): React.JSX.Element => (
    <span
      key={h}
      onPointerDown={resize ? (e) => resize.onDown(h, e) : undefined}
      onPointerMove={resize?.onMove}
      onPointerUp={resize?.onUp}
      onPointerCancel={resize?.onUp}
      style={{
        position: "absolute", width: 11, height: 11, background: accent, borderRadius: 3,
        boxShadow: "0 0 0 2px rgba(0,0,0,.45)", touchAction: "none",
        pointerEvents: resize ? "auto" : "none", cursor: resize ? cursor : undefined,
        ...pos,
      }}
    />
  );
  return (
    <div
      className="le-selframe"
      style={{
        position: "absolute",
        inset: -8,
        border: `2px solid ${accent}`,
        borderRadius: 9,
        boxShadow: `0 0 0 1px rgba(0,0,0,.35), 0 0 16px 1px ${hexToRgba(accent, 0.35)}`,
        pointerEvents: "none",
      }}
    >
      {handle("tl", { top: -6, left: -6 }, "nwse-resize")}
      {handle("tr", { top: -6, right: -6 }, "nesw-resize")}
      {handle("bl", { bottom: -6, left: -6 }, "nesw-resize")}
      {handle("br", { bottom: -6, right: -6 }, "nwse-resize")}
      {sideHandles && handle("ml", { top: "50%", left: -6, transform: "translateY(-50%)" }, "ew-resize")}
      {sideHandles && handle("mr", { top: "50%", right: -6, transform: "translateY(-50%)" }, "ew-resize")}
    </div>
  );
}

function LayerChip({ icon, label, active, accent, onClick }: { icon: React.ReactNode; label: string; active: boolean; accent: string; onClick: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      className="le-chip"
      onClick={onClick}
      style={{
        flex: 1,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        padding: "10px 8px",
        borderRadius: 11,
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: 13,
        fontWeight: 700,
        border: "1px solid " + (active ? accent : "transparent"),
        background: active ? hexToRgba(accent, 0.1) : "transparent",
        color: active ? "var(--ql-fg)" : "var(--ql-fg-muted)",
      }}
    >
      <span style={{ color: active ? accent : "var(--ql-fg-subtle)", display: "inline-flex" }}>{icon}</span>
      {label}
    </button>
  );
}

function PropertyPanel({ layer, values, onChange, accent, logoDataUrl, capLine, onCapLine, fontOptions }: {
  layer: LayerId | null;
  values: LayoutValues;
  onChange: (patch: Partial<LayoutValues>) => void;
  accent: string;
  logoDataUrl: string | null;
  capLine: "top" | "punch";
  onCapLine: (v: "top" | "punch") => void;
  fontOptions: string[];
}): React.JSX.Element {
  const panelStyle: React.CSSProperties = {
    background: "rgba(255,255,255,.02)",
    border: "1px solid rgba(255,255,255,.06)",
    borderRadius: 16,
    padding: 18,
    display: "grid",
    gap: 16,
    alignContent: "start",
  };

  if (!layer) {
    return (
      <div style={{ ...panelStyle, minHeight: 160, alignContent: "center", justifyItems: "center", textAlign: "center", gap: 10 }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: hexToRgba(accent, 0.1), display: "grid", placeItems: "center", color: accent }}>
          <Move size={18} />
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ql-fg)" }}>Nothing selected</div>
        <div style={{ fontSize: 12.5, color: "var(--ql-fg-muted)", lineHeight: 1.6, maxWidth: 240 }}>
          Pick a layer above — or click any element on the canvas — to drag it into place and tune its style.
        </div>
      </div>
    );
  }

  const header = (icon: React.ReactNode, title: string, readout: string): React.JSX.Element => (
    <div style={{ display: "grid", gap: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
        <span style={{ width: 28, height: 28, borderRadius: 9, background: hexToRgba(accent, 0.12), display: "grid", placeItems: "center", color: accent }}>{icon}</span>
        <span style={{ fontSize: 15, fontWeight: 700, color: "var(--ql-fg)" }}>{title}</span>
      </div>
      <div style={{ fontSize: 11.5, color: "var(--ql-fg-subtle)", fontFamily: "var(--ql-font-mono)", paddingLeft: 37 }}>{readout}</div>
    </div>
  );

  if (layer === "caption") {
    const uniform = values.capUniform;
    const activeTop = capLine === "top";
    const lineSize = uniform ? values.capFontSizePx : activeTop ? values.capTopPx : values.capPunchPx;
    const lineColor = uniform || activeTop ? values.captionColor : values.captionAccent;
    const sizeMin = uniform ? 50 : activeTop ? 40 : 60;
    const sizeMax = uniform ? 260 : activeTop ? 200 : 340;
    const sizeLabel = uniform ? "Font size" : activeTop ? "Line 1 size" : "Line 2 size";
    const colorLabel = uniform ? "Colour" : activeTop ? "Line 1 colour" : "Line 2 colour";
    const setSize = (v: number): void => onChange(uniform ? { capFontSizePx: v } : activeTop ? { capTopPx: v } : { capPunchPx: v });
    const setColor = (v: string): void => onChange(uniform || activeTop ? { captionColor: v } : { captionAccent: v });
    const lineFont = uniform || activeTop ? values.fontCaption : values.fontPunch;
    const setFont = (v: string): void => onChange(uniform || activeTop ? { fontCaption: v } : { fontPunch: v });
    return (
      <div style={panelStyle}>
        {header(<Captions size={15} />, "Caption", uniform ? "single line" : activeTop ? "editing line 1 (top)" : "editing line 2 (punch)")}
        {!uniform && <LineTabs value={capLine} accent={accent} onChange={onCapLine} />}
        <Slider label={sizeLabel} value={lineSize} min={sizeMin} max={sizeMax} step={5} accent={accent} fmt={(v) => `${v}px`} onChange={setSize} />
        <ColorRow label={colorLabel} value={lineColor} onChange={setColor} />
        {fontOptions.length > 0 && (
          <FontSelect label={uniform ? "Font" : activeTop ? "Line 1 font" : "Line 2 font"} value={lineFont} options={fontOptions} onChange={setFont} />
        )}
        <div style={{ height: 1, background: "rgba(255,255,255,.07)", marginTop: 2 }} />
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--ql-fg-subtle)" }}>Advanced</span>
        <Toggle label="Uniform single-size lines" hint="Every line one size — no big-punch bottom." on={values.capUniform} accent={accent} onChange={(v) => onChange({ capUniform: v })} />
        <Toggle label="Flat text (no shadow)" hint="Clean text, no drop-shadow. Pair with a box." on={values.capFlatText} accent={accent} onChange={(v) => onChange({ capFlatText: v })} />
        <Toggle label="Background box" hint="Rounded translucent box behind text." on={values.capBox} accent={accent} onChange={(v) => onChange({ capBox: v })} />
        {values.capBox && <Slider label="Box opacity" value={values.capBoxOpacity} min={0} max={1} step={0.02} accent={accent} fmt={(v) => `${Math.round(v * 100)}%`} onChange={(v) => onChange({ capBoxOpacity: v })} />}
        <Slider label="Line spacing" value={values.capLineHeight} min={0.9} max={2} step={0.05} accent={accent} fmt={(v) => v.toFixed(2)} onChange={(v) => onChange({ capLineHeight: v })} />
        <Slider label="Letter spacing" value={values.capLetterSpacingPx} min={-3} max={6} step={0.25} accent={accent} fmt={(v) => `${v} px`} onChange={(v) => onChange({ capLetterSpacingPx: v })} />
        <Slider label="Side padding" value={values.capSidePaddingPx} min={20} max={400} step={10} accent={accent} fmt={(v) => `${v}px`} onChange={(v) => onChange({ capSidePaddingPx: v })} />
      </div>
    );
  }

  if (layer === "logo") {
    return (
      <div style={panelStyle}>
        {header(<ImageIcon size={15} />, "Logo", `x ${values.logoXPct}% · ${values.logoBottomPx}px up`)}
        {!logoDataUrl && (
          <div style={{ fontSize: 12.5, color: "var(--ql-fg-muted)", lineHeight: 1.55, padding: "10px 12px", borderRadius: 10, border: `1px solid ${hexToRgba(accent, 0.22)}`, background: hexToRgba(accent, 0.05) }}>
            Upload a logo above to see it here — the placeholder box is draggable so you can pre-place it.
          </div>
        )}
        <Slider label="Width" value={values.logoWidthPct} min={5} max={80} step={1} accent={accent} fmt={(v) => `${v}%`} onChange={(v) => onChange({ logoWidthPct: v })} />
        <Slider label="Opacity" value={values.logoOpacity} min={0} max={1} step={0.05} accent={accent} fmt={(v) => `${Math.round(v * 100)}%`} onChange={(v) => onChange({ logoOpacity: v })} />
      </div>
    );
  }

  // speaker
  return (
    <div style={panelStyle}>
      {header(<User size={15} />, "Speaker card", `x ${values.speakerCardXPct}% · y ${values.speakerCardYPct}% · ${Math.round(values.speakerCardScale * 100)}%`)}
      <Slider label="Card size" value={values.speakerCardScale} min={0.5} max={2} step={0.05} accent={accent} fmt={(v) => `${Math.round(v * 100)}%`} onChange={(v) => onChange({ speakerCardScale: v })} />
      {fontOptions.length > 0 && (
        <FontSelect label="Speaker font" value={values.fontSpeaker} options={fontOptions} onChange={(v) => onChange({ fontSpeaker: v })} />
      )}
      <div style={{ fontSize: 12.5, color: "var(--ql-fg-muted)", lineHeight: 1.6 }}>
        Drag the card on the canvas to place it. The <strong>name and role</strong> are filled in automatically from each clip — this is a sample.
      </div>
    </div>
  );
}

/** Two tabs to pick which caption line the panel edits (mirrors clicking a line on the canvas). */
function LineTabs({ value, accent, onChange }: { value: "top" | "punch"; accent: string; onChange: (v: "top" | "punch") => void }): React.JSX.Element {
  const tab = (v: "top" | "punch", label: string): React.JSX.Element => (
    <button
      type="button"
      onClick={() => onChange(v)}
      style={{
        flex: 1, padding: "9px 6px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit",
        fontSize: 12.5, fontWeight: 700,
        border: "1px solid " + (value === v ? accent : "rgba(255,255,255,.08)"),
        background: value === v ? hexToRgba(accent, 0.14) : "rgba(255,255,255,.03)",
        color: value === v ? "var(--ql-fg)" : "var(--ql-fg-muted)",
      }}
    >
      {label}
    </button>
  );
  return <div style={{ display: "flex", gap: 6 }}>{tab("top", "Line 1 · top")}{tab("punch", "Line 2 · punch")}</div>;
}

/** A labelled font-family dropdown; "" = Auto (channel default). Each row
 *  renders in its own face so the choice is visible before applying — and the
 *  trigger shows the picked family the same way. */
function FontSelect({ label, value, options, onChange }: {
  label: string; value: string; options: string[]; onChange: (v: string) => void;
}): React.JSX.Element {
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 9 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ql-fg-muted)", textTransform: "uppercase", letterSpacing: ".06em" }}>{label}</span>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ql-fg)", fontFamily: "var(--ql-font-mono)" }}>{value || "auto"}</span>
      </div>
      <Select
        value={value}
        onChange={onChange}
        aria-label={label}
        options={[
          { value: "", label: "Auto (channel default)" },
          ...options.map((f) => ({ value: f, label: f, style: { fontFamily: `"${f}", sans-serif` } })),
        ]}
      />
    </div>
  );
}

/** A labelled native colour swatch that reports the picked hex live. */
function ColorRow({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }): React.JSX.Element {
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 9 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ql-fg-muted)", textTransform: "uppercase", letterSpacing: ".06em" }}>{label}</span>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ql-fg)", fontFamily: "var(--ql-font-mono)" }}>{value.toUpperCase()}</span>
      </div>
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: "100%", height: 34, borderRadius: 9, border: "1px solid rgba(255,255,255,.12)", background: "transparent", cursor: "pointer", padding: 2 }}
      />
    </div>
  );
}

function Slider({ label, value, min, max, step, accent, fmt, onChange }: {
  label: string; value: number; min: number; max: number; step: number; accent: string; fmt: (v: number) => string; onChange: (v: number) => void;
}): React.JSX.Element {
  const pct = max > min ? ((value - min) / (max - min)) * 100 : 0;
  const sliderStyle = {
    "--le-accent": accent,
    background: `linear-gradient(to right, ${accent} 0%, ${accent} ${pct}%, rgba(255,255,255,.10) ${pct}%, rgba(255,255,255,.10) 100%)`,
  } as React.CSSProperties;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 9 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ql-fg-muted)", textTransform: "uppercase", letterSpacing: ".06em" }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ql-fg)", fontFamily: "var(--ql-font-mono)" }}>{fmt(value)}</span>
      </div>
      <input
        type="range"
        className="le-range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={sliderStyle}
      />
    </div>
  );
}

function Toggle({ label, hint, on, accent, onChange }: { label: string; hint: string; on: boolean; accent: string; onChange: (v: boolean) => void }): React.JSX.Element {
  return (
    <label style={{
      display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 14px",
      border: "1px solid " + (on ? hexToRgba(accent, 0.35) : "rgba(255,255,255,.06)"), borderRadius: 12,
      background: on ? hexToRgba(accent, 0.05) : "rgba(255,255,255,.02)", cursor: "pointer",
    }}>
      <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked)} style={{ marginTop: 3, accentColor: accent }} />
      <div>
        <div style={{ fontWeight: 600, fontSize: 13.5, color: "var(--ql-fg)" }}>{label}</div>
        <div style={{ fontSize: 11.5, color: "var(--ql-fg-muted)", marginTop: 3, lineHeight: 1.5 }}>{hint}</div>
      </div>
    </label>
  );
}
